import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useApi } from '@/api/ApiContext';
import { ALL_BRANDS_QUERY } from '@/api/handlers/resources/brands/requests';
import { BrandQueryKeys } from '@/api/queries/brands/brandQueryKeys';
import { CategoryQueryKeys } from '@/api/queries/categories/categoryQueryKeys';
import { SupplierQueryKeys } from '@/api/queries/suppliers/supplierQueryKeys';
import { flattenPickerCategories } from '@/pages/products/components/productsMeta';
import { PATH } from '@/router/PATH';
import { downloadBlob, xlsxBlobOf } from '@/utils/fileDownload';
import {
    buildPurchaseTemplateWorkbook,
    getTemplateFilename,
} from './excelTemplate';
import {
    TEMPLATE_COLUMNS,
    type PurchaseTemplateData,
    type PurchaseTemplateLabels,
} from './purchaseTemplateMeta';
import type { PurchaseHeaderActionKey } from './purchaseHeaderMeta';

/**
 * Drives the three Purchases-header actions.
 *
 * `excelTemplate` is the fully-wired one: it lazily fetches the supplier /
 * category / brand lists (via the shared query cache, so no eager fetch on page
 * load), builds the dropdown-backed workbook and triggers the download. exceljs
 * is dynamically imported inside the builder, so the heavy dependency only
 * loads when a template is actually generated. `manualImport` navigates;
 * `excelImport` opens the upload modal (rendered by the container).
 */
export const usePurchaseHeader = () => {
    const { t, i18n } = useTranslation('pages');
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { apiClient } = useApi();

    const [isGeneratingTemplate, setIsGeneratingTemplate] =
        React.useState(false);
    const [isExcelImportOpen, setIsExcelImportOpen] = React.useState(false);
    const [templateFailed, setTemplateFailed] = React.useState(false);

    /**
     * Last generated template, keyed by language + the three source query
     * results **by reference**. TanStack Query hands back the same cached
     * object until a source actually refetches, and every supplier / category /
     * brand mutation invalidates its list key — so a reference match means the
     * data is provably unchanged and the Blob can be re-served as is
     * (regeneration costs ~50 ms; this keeps repeat downloads instant and
     * allocation-free).
     */
    const templateCacheRef = React.useRef<{
        language: string;
        sources: readonly [unknown, unknown, unknown];
        blob: Blob;
    } | null>(null);

    // Pull the dropdown source lists from the shared cache (fetch on first use).
    const fetchTemplateData = React.useCallback(async (): Promise<{
        data: PurchaseTemplateData;
        sources: readonly [unknown, unknown, unknown];
    }> => {
        const [suppliers, categories, brands] = await Promise.all([
            queryClient.ensureQueryData({
                queryKey: SupplierQueryKeys.picker(),
                queryFn: () => apiClient.suppliers.getSupplierPicker(),
            }),
            queryClient.ensureQueryData({
                queryKey: CategoryQueryKeys.picker(),
                queryFn: () => apiClient.categories.getCategoryPicker(),
            }),
            queryClient.ensureQueryData({
                queryKey: BrandQueryKeys.listPage(ALL_BRANDS_QUERY),
                queryFn: () => apiClient.brands.getBrandList(ALL_BRANDS_QUERY),
            }),
        ]);

        return {
            data: {
                suppliers: suppliers.success
                    ? suppliers.response.items.map((item) => item.name)
                    : [],
                categories: categories.success
                    ? flattenPickerCategories(categories.response.items).map(
                          (option) => option.name,
                      )
                    : [],
                brands: brands.success
                    ? brands.response.items.map((item) => item.name)
                    : [],
            },
            sources: [suppliers, categories, brands] as const,
        };
    }, [apiClient, queryClient]);

    const buildLabels = React.useCallback(
        (): PurchaseTemplateLabels => ({
            columns: Object.fromEntries(
                TEMPLATE_COLUMNS.map((column) => [
                    column.key,
                    t(column.labelKey),
                ]),
            ),
            sheets: {
                products: t('purchases.template.sheets.products'),
                suppliers: t('purchases.template.sheets.suppliers'),
                categories: t('purchases.template.sheets.categories'),
                brands: t('purchases.template.sheets.brands'),
            },
        }),
        [t],
    );

    const downloadTemplate = React.useCallback(async () => {
        setIsGeneratingTemplate(true);
        setTemplateFailed(false);
        try {
            const { data, sources } = await fetchTemplateData();

            const cached = templateCacheRef.current;
            const isCacheHit =
                cached !== null &&
                cached.language === i18n.language &&
                cached.sources.every(
                    (source, index) => source === sources[index],
                );

            let blob: Blob;
            if (isCacheHit) {
                blob = cached.blob;
            } else {
                const workbook = await buildPurchaseTemplateWorkbook(
                    data,
                    buildLabels(),
                );
                blob = await xlsxBlobOf(workbook);
                templateCacheRef.current = {
                    language: i18n.language,
                    sources,
                    blob,
                };
            }

            downloadBlob(blob, getTemplateFilename(i18n.language));
        } catch (error) {
            // The console line is for whoever is debugging; the flag is for
            // whoever pressed the button. Without it the spinner stopped, no
            // file arrived, and the screen said nothing at all — which reads
            // as a dead button, not as a failure worth retrying.
            console.error(
                '[purchases] failed to generate the Excel template:',
                error,
            );
            setTemplateFailed(true);
        } finally {
            setIsGeneratingTemplate(false);
        }
    }, [buildLabels, fetchTemplateData, i18n.language]);

    const onAction = React.useCallback(
        (key: PurchaseHeaderActionKey) => {
            if (key === 'excelTemplate') {
                void downloadTemplate();
                return;
            }

            if (key === 'excelImport') {
                setIsExcelImportOpen(true);
                return;
            }

            if (key === 'manualImport') {
                navigate(PATH.PURCHASES.MANUAL_IMPORT);
            }
        },
        [downloadTemplate, navigate],
    );

    const onDismissTemplateError = React.useCallback(() => {
        setTemplateFailed(false);
    }, []);

    return {
        onAction,
        isGeneratingTemplate,
        isExcelImportOpen,
        onExcelImportOpenChange: setIsExcelImportOpen,
        templateFailed,
        onDismissTemplateError,
    } as const;
};
